import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Flower, ChatCircle, HandHeart, ShoppingBag, SignOut, User, Article, ShieldCheck } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exchangeStatus, setExchangeStatus] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [userRes, exchangeRes] = await Promise.all([
          axios.get(`${API}/auth/me`, { withCredentials: true }),
          axios.get(`${API}/flower-exchange/my-registration`, { withCredentials: true })
        ]);
        setUser(userRes.data);
        setExchangeStatus(exchangeRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      toast.success('Logged out successfully');
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Failed to logout');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-xl font-heading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <nav className="bg-white/70 backdrop-blur-lg border-b border-white/20 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Flower size={32} weight="fill" className="text-primary" />
            <span className="text-2xl font-heading font-bold">Bloom & Bond</span>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <img src={user?.picture} alt={user?.name} className="w-10 h-10 rounded-full" />
              <span className="text-sm font-medium hidden md:block">{user?.name}</span>
            </div>
            <Button 
              data-testid="logout-button"
              onClick={handleLogout}
              variant="outline"
              size="sm"
              className="rounded-full"
            >
              <SignOut size={20} className="mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl md:text-5xl font-heading font-bold mb-2">Welcome back, {user?.name?.split(' ')[0]}! 🌸</h1>
          <p className="text-muted-foreground text-lg mb-12">What would you like to do today?</p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <motion.div
              whileHover={{ scale: 1.02, y: -4 }}
              className="bg-white/80 backdrop-blur-md border border-white/20 shadow-sm rounded-2xl p-8 cursor-pointer hover:shadow-md transition-all duration-300"
              onClick={() => navigate('/feed')}
              data-testid="feed-card"
            >
              <Article size={48} weight="fill" className="text-secondary mb-4" />
              <h3 className="text-xl font-heading font-semibold mb-2">Community Feed</h3>
              <p className="text-muted-foreground text-sm">Share posts and updates</p>
            </motion.div>

            <motion.div
              whileHover={{ scale: 1.02, y: -4 }}
              className="bg-white/80 backdrop-blur-md border border-white/20 shadow-sm rounded-2xl p-8 cursor-pointer hover:shadow-md transition-all duration-300"
              onClick={() => navigate('/chat')}
              data-testid="chat-card"
            >
              <ChatCircle size={48} weight="fill" className="text-primary mb-4" />
              <h3 className="text-xl font-heading font-semibold mb-2">Live Chat</h3>
              <p className="text-muted-foreground text-sm">Connect in real-time</p>
            </motion.div>

            <motion.div
              whileHover={{ scale: 1.02, y: -4 }}
              className="bg-white/80 backdrop-blur-md border border-white/20 shadow-sm rounded-2xl p-8 cursor-pointer hover:shadow-md transition-all duration-300"
              onClick={() => navigate('/flower-exchange')}
              data-testid="flower-exchange-card"
            >
              <Flower size={48} weight="fill" className="text-secondary" />
              <h3 className="text-xl font-heading font-semibold mb-2">Flower Exchange</h3>
              <p className="text-muted-foreground text-sm">
                {exchangeStatus ? 'View your registration' : 'Sign up for the event'}
              </p>
              {exchangeStatus && (
                <div className="mt-3 inline-block bg-accent/20 text-accent px-3 py-1 rounded-full text-xs font-medium">
                  Registered ✓
                </div>
              )}
            </motion.div>

            <motion.div
              whileHover={{ scale: 1.02, y: -4 }}
              className="bg-white/80 backdrop-blur-md border border-white/20 shadow-sm rounded-2xl p-8 cursor-pointer hover:shadow-md transition-all duration-300"
              onClick={() => navigate('/donate')}
              data-testid="donate-card"
            >
              <HandHeart size={48} weight="fill" className="text-accent" />
              <h3 className="text-xl font-heading font-semibold mb-2">Donate</h3>
              <p className="text-muted-foreground text-sm">Support our community</p>
            </motion.div>

            <motion.div
              whileHover={{ scale: 1.02, y: -4 }}
              className="bg-white/80 backdrop-blur-md border border-white/20 shadow-sm rounded-2xl p-8 cursor-pointer hover:shadow-md transition-all duration-300"
              onClick={() => navigate('/merch')}
              data-testid="merch-card"
            >
              <ShoppingBag size={48} weight="fill" className="text-primary" />
              <h3 className="text-xl font-heading font-semibold mb-2">Shop Merch</h3>
              <p className="text-muted-foreground text-sm">Get exclusive community items</p>
            </motion.div>
          </div>

          <div className="bg-gradient-to-br from-[#FF9A9E] to-[#FECFEF] rounded-3xl p-8 md:p-12">
            <h2 className="text-3xl font-heading font-bold text-white mb-4">Upcoming: Yearly Flower Exchange February 2026</h2>
            <p className="text-white/90 mb-6 max-w-2xl">
              Join our annual tradition of spreading joy! Exchange beautiful floral arrangements with fellow members. 
              {!exchangeStatus && ' Registration is now open!'}
            </p>
            {!exchangeStatus && (
              <Button 
                data-testid="register-flower-exchange-button"
                onClick={() => navigate('/flower-exchange')}
                className="rounded-full bg-white text-primary hover:bg-white/90 font-bold px-8"
              >
                Register Now
              </Button>
            )}
            {exchangeStatus && (
              <div className="inline-block bg-white/20 backdrop-blur-sm text-white px-6 py-3 rounded-full font-medium">
                You're registered! Check your email for updates.
              </div>
            )}
          </div>

          <div className="mt-8 bg-white/80 backdrop-blur-md border border-white/20 shadow-sm rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <ShieldCheck size={32} weight="fill" className="text-accent" />
              <h3 className="text-xl font-heading font-semibold">Admin Tools</h3>
            </div>
            <p className="text-muted-foreground mb-4 text-sm">
              Manage your community and view registrations
            </p>
            <Button
              data-testid="admin-flower-exchange-button"
              onClick={() => navigate('/admin/login')}
              variant="outline"
              className="rounded-full"
            >
              Admin Login
            </Button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}